import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Popover,
  TextField,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { TableVirtuoso } from 'react-virtuoso'
import {
  type SettingRole,
  type SettingUser,
  type SettingUserCreatePayload,
  type SettingUserUpdatePayload,
  useChangeSettingUserPasswordMutation,
  useCreateSettingUserMutation,
  useGetSettingRolesQuery,
  useGetSettingUsersQuery,
  useUpdateSettingUserMutation,
} from '../../store/api/settingsApi'
import { usePagePermission } from '../../hooks/usePagePermission'
import SettingsChangePasswordDialog from './SettingsChangePasswordDialog'
import SettingsUserDeleteDialog from './SettingsUserDeleteDialog'
import SettingsUserDialog, { type SettingsUserDialogForm } from './SettingsUserDialog'
import SettingsUserValidDialog from './SettingsUserValidDialog'

type ColumnKind = 'text' | 'date' | 'datetime'
type ColumnDef = { key: keyof SettingUser; label: string; kind?: ColumnKind; align?: 'right' }

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'username', label: 'Username' },
  { key: 'description', label: 'Description' },
  { key: 'roleCode', label: 'Role Code' },
  { key: 'roleName', label: 'Role Name' },
  { key: 'email', label: 'Email' },
  { key: 'fullName', label: 'Full Name' },
  { key: 'phone', label: 'Phone' },
  { key: 'validText', label: 'Valid Text' },
  { key: 'dateUpdate', label: 'Updated Date', kind: 'datetime' },
  { key: 'personUpdate', label: 'Updated Person' },
]

const actionToolbarBtnSx = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

const filterBarBtnSx = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  textTransform: 'none',
  fontSize: '0.7rem',
  lineHeight: 1.1,
}

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  return `${day}-${month}-${year}`
}

const formatAccessDateTime = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}

const extractApiErrorMessage = (error: unknown): string => {
  if (!error || typeof error !== 'object') return 'Failed to save user. Please try again.'
  if (!('data' in error)) return 'Failed to save user. Please try again.'

  const data = (error as { data?: unknown }).data
  if (!data || typeof data !== 'object') return 'Failed to save user. Please try again.'

  const detail = (data as { detail?: unknown }).detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0]
    if (typeof first === 'string') return first
    if (first && typeof first === 'object' && 'msg' in first) {
      const msg = (first as { msg?: unknown }).msg
      if (typeof msg === 'string' && msg.trim()) return msg
    }
    return JSON.stringify(first)
  }
  if (detail && typeof detail === 'object') return JSON.stringify(detail)

  return 'Failed to save user. Please try again.'
}

const cellValue = (row: SettingUser, col: ColumnDef): string => {
  const val = row[col.key]
  if (val == null) return ''
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'datetime') return formatAccessDateTime(val)
  return String(val)
}

export default function SettingsAccountListView() {
  const { canEdit } = usePagePermission()
  const [selectedUsername, setSelectedUsername] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const [searchTerm, setSearchTerm] = useState('')
  const [searchAnchorEl, setSearchAnchorEl] = useState<HTMLElement | null>(null)

  const [userDialogOpen, setUserDialogOpen] = useState(false)
  const [userDialogMode, setUserDialogMode] = useState<'new' | 'edit'>('new')
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [validDialogOpen, setValidDialogOpen] = useState(false)

  const { data: usersData, isLoading, isFetching, error } = useGetSettingUsersQuery()
  const { data: rolesData } = useGetSettingRolesQuery()

  const [createUser, { isLoading: isCreating }] = useCreateSettingUserMutation()
  const [updateUser, { isLoading: isUpdating }] = useUpdateSettingUserMutation()
  const [changePassword, { isLoading: isChangingPassword }] = useChangeSettingUserPasswordMutation()

  const baseRows = useMemo(() => usersData?.items ?? [], [usersData])
  const filteredRows = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase()
    if (!needle) return baseRows
    return baseRows.filter((row) =>
      TABLE_COLUMNS.some((col) => {
        const value = cellValue(row, col)
        return value.toLowerCase().includes(needle)
      }),
    )
  }, [baseRows, searchTerm])
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return filteredRows
    const start = page * rowsPerPage
    return filteredRows.slice(start, start + rowsPerPage)
  }, [filteredRows, page, rowsPerPage])
  const selectedRow = useMemo(
    () => baseRows.find((row) => row.username === selectedUsername) ?? null,
    [baseRows, selectedUsername],
  )
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}`
  const roleOptions = useMemo(() => rolesData?.items ?? [], [rolesData])
  const hasSelection = Boolean(selectedRow)

  useEffect(() => {
    if (selectedUsername && !baseRows.some((row) => row.username === selectedUsername)) {
      setSelectedUsername(null)
    }
  }, [baseRows, selectedUsername])

  useEffect(() => {
    setPage(0)
  }, [searchTerm])

  const handleOpenUserDialog = (mode: 'new' | 'edit') => {
    if (mode === 'edit' && !selectedRow) return
    setUserDialogMode(mode)
    setUserDialogOpen(true)
  }

  const handleOpenPasswordDialog = () => {
    if (!selectedRow) return
    setPasswordDialogOpen(true)
  }

  const handleOpenDeleteDialog = () => {
    if (!selectedRow) return
    setDeleteDialogOpen(true)
  }

  const handleOpenValidDialog = () => {
    if (!selectedRow) return
    setValidDialogOpen(true)
  }

  const handleSaveUser = async (form: SettingsUserDialogForm) => {
    const isNew = userDialogMode === 'new'
    if (isNew) {
      const payload: SettingUserCreatePayload = {
        email: form.email.trim(),
        password: form.password,
        confirmPassword: form.confirmPassword,
        fullName: form.fullName.trim(),
        phone: form.phone.trim() ? form.phone.trim() : null,
        roleCode: form.roleCode.trim(),
        description: form.description.trim() ? form.description.trim() : null,
      }
      const saved = await createUser(payload).unwrap()
      setSelectedUsername(saved.username)
      return
    }

    if (!selectedRow) {
      throw new Error('Please choose a record!')
    }

    const payload: SettingUserUpdatePayload = {
      fullName: form.fullName.trim(),
      phone: form.phone.trim() ? form.phone.trim() : null,
      roleCode: form.roleCode.trim(),
      description: form.description.trim() ? form.description.trim() : null,
    }
    const saved = await updateUser({ email: selectedRow.email, body: payload }).unwrap()
    setSelectedUsername(saved.username)
  }

  const handleSavePassword = async (payload: { password: string; confirmPassword: string }) => {
    if (!selectedRow) {
      throw new Error('Please choose a record!')
    }
    await changePassword({
      email: selectedRow.email,
      body: payload,
    }).unwrap()
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center" sx={{ mb: 0.5 }}>
        <Stack direction="row" spacing={0.375} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mr: 'auto', minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25, fontWeight: 400 }}>
            Filter
          </Typography>
          <Button
            size="small"
            variant={searchTerm.trim() ? 'contained' : 'outlined'}
            color={searchTerm.trim() ? 'primary' : 'inherit'}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => setSearchAnchorEl(e.currentTarget)}
            sx={filterBarBtnSx}
          >
            Search
          </Button>
        </Stack>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
          <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={() => handleOpenUserDialog('new')}>
            New
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="warning"
            sx={actionToolbarBtnSx}
            disabled={!hasSelection || !canEdit}
            onClick={() => handleOpenUserDialog('edit')}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            sx={actionToolbarBtnSx}
            disabled={!hasSelection || !canEdit}
            onClick={handleOpenDeleteDialog}
          >
            Delete
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
            disabled={!hasSelection || !canEdit}
            onClick={handleOpenPasswordDialog}
          >
            Change Password
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
            disabled={!hasSelection || !canEdit}
            onClick={handleOpenValidDialog}
          >
            Valid/Invalid Account
          </Button>
        </Stack>
      </Stack>

      <Divider sx={{ mb: 0.5 }} />

      <Popover
        open={searchAnchorEl !== null}
        anchorEl={searchAnchorEl}
        onClose={() => setSearchAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 1.5, minWidth: 240, maxWidth: 320 } }}
      >
        <Stack spacing={1}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            label="Search users"
            placeholder="Username, email, role, name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Button
            size="small"
            variant="text"
            color="secondary"
            onClick={() => {
              setSearchTerm('')
              setSearchAnchorEl(null)
            }}
          >
            Clear Search
          </Button>
        </Stack>
      </Popover>

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          {extractApiErrorMessage(error)}
        </Alert>
      ) : null}

      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
            flexWrap: 'wrap',
            minHeight: 30,
            py: 0.125,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}>
              Loaded: {baseRows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={{
                flexShrink: 0,
                '& .MuiTablePagination-toolbar': { minHeight: 32, px: 0.5 },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.72rem' },
                '& .MuiTablePagination-select': { fontSize: '0.72rem' },
                '& .MuiTablePagination-actions': { transform: 'scale(0.92)', transformOrigin: 'center' },
              }}
            />
          </Stack>
        </Box>

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          <TableVirtuoso
            key={gridKey}
            style={{ height: '100%' }}
            data={rows}
            fixedHeaderContent={() => (
              <TableRow>
                {TABLE_COLUMNS.map((col) => (
                  <TableCell key={col.key} align={col.align} sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'background.default' }}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            )}
            itemContent={(_: number, row: SettingUser) =>
              TABLE_COLUMNS.map((col) => {
                const val = cellValue(row, col)
                return (
                  <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap', py: 0.5 }}>
                    <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%' }}>
                      {val || '-'}
                    </Box>
                  </TableCell>
                )
              })
            }
            components={{
              Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: 1500, width: '100%' }} />,
              TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
              TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
              TableRow: (props: any) => {
                const idx = Number((props as any)['data-item-index'])
                const rowUsername = Number.isFinite(idx) ? rows[idx]?.username : null
                return (
                  <TableRow
                    {...props}
                    hover
                    selected={rowUsername != null && rowUsername === selectedUsername}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      if (rowUsername != null) setSelectedUsername(rowUsername)
                    }}
                  />
                )
              },
            }}
          />
        </Box>
      </Box>

      <SettingsUserDialog
        open={userDialogOpen}
        mode={userDialogMode}
        initialUser={selectedRow}
        roles={roleOptions}
        onClose={() => setUserDialogOpen(false)}
        onSubmit={handleSaveUser}
        isSubmitting={isCreating || isUpdating}
      />
      <SettingsChangePasswordDialog
        open={passwordDialogOpen}
        email={selectedRow?.email ?? ''}
        onClose={() => setPasswordDialogOpen(false)}
        onSubmit={handleSavePassword}
        isSubmitting={isChangingPassword}
      />
      <SettingsUserDeleteDialog
        open={deleteDialogOpen}
        email={selectedRow?.email ?? ''}
        onClose={() => setDeleteDialogOpen(false)}
        onDeleted={() => {
          setSelectedUsername(null)
          setDeleteDialogOpen(false)
        }}
      />
      <SettingsUserValidDialog
        open={validDialogOpen}
        email={selectedRow?.email ?? ''}
        nextValidText={selectedRow?.validText === 'Valid' ? 'invalid' : 'valid'}
        onClose={() => setValidDialogOpen(false)}
        onConfirmed={() => {
          setValidDialogOpen(false)
        }}
      />
    </Box>
  )
}

